import React, { useState, useEffect, Fragment, useRef } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import api, { getFileUrl as getApiFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import Select from "react-select";

const DocumentControlUpload = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const location = useLocation();
 const [searchParams] = useSearchParams();
 const type = location.pathname.includes("/tds") ? "TDS" : "TC";
 const initialProject = searchParams.get("project") || "";
 const initialWorkOrder = searchParams.get("workOrder") || "";
 const listPath = type === "TDS" ? "/tds" : "/tc-documents";

 const getReturnUrl = () => {
 const params = new URLSearchParams();
 const workOrder = searchParams.get("workOrder") || initialWorkOrder;

 if (workOrder) params.set("workOrder", workOrder);

 return `${listPath}${params.toString() ? `?${params.toString()}` : ""}`;
 };

 const [workOrders, setWorkOrders] = useState([]);
 const [purchaseOrders, setPurchaseOrders] = useState([]);
 const [boqItems, setBoqItems] = useState([]);
 const [loading, setLoading] = useState(false);
 const [loadingWO, setLoadingWO] = useState(false);
 const [loadingDoc, setLoadingDoc] = useState(false);
 const [editMode, setEditMode] = useState(false);

 const [form, setForm] = useState({
 project: "",
 workOrder: initialWorkOrder,
 purchaseOrder: "",
 boqItems: [],
 boqItem: "",
 wlino: "",
 productName: "",
 quom: "",
 qty: "",
 docCategory: type === "TC" ? "TC" : "TDS",
 submittedDate: new Date().toISOString().split("T")[0],
 approvedDate: "",
 status: "Pending Approval",
 remarks: "",
 });

 const [documents, setDocuments] = useState([]);
 const [existingDocuments, setExistingDocuments] = useState([]);

 // Local state for new documents being uploaded
 const [newAttachments, setNewAttachments] = useState([]);

 const handleFileChange = (e) => {
 const files = Array.from(e.target.files);
 const newItems = files.map((file) => ({
 file,
 fileName: file.name,
 docType: type === "TDS" ? "TDS" : (form.docCategory || "TC"),
 remarks: "",
 }));
 setNewAttachments((prev) => [...prev, ...newItems]);
 // Clear input
 e.target.value = null;
 };

 const removeNewAttachment = (index) => {
 setNewAttachments((prev) => prev.filter((_, i) => i !== index));
 };

 const updateNewAttachment = (index, field, value) => {
 setNewAttachments((prev) =>
 prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
 );
 };

 useEffect(() => {
 fetchWorkOrders(initialProject);
 if (id) {
 setEditMode(true);
 fetchDocument(id);
 }
 }, [id, initialProject]);

 useEffect(() => {
 if (initialProject || initialWorkOrder) {
 setForm((prev) => ({
 ...prev,
 project: initialProject || prev.project,
 workOrder: initialWorkOrder || prev.workOrder,
 }));
 }
 }, [initialProject, initialWorkOrder]);

 useEffect(() => {
 if (!editMode && initialWorkOrder) {
 fetchBoqItems(initialWorkOrder);
 fetchPurchaseOrders(initialWorkOrder);
 }
 }, [editMode, initialWorkOrder]);

 const fetchWorkOrders = async (projectId = "") => {
 try {
 setLoadingWO(true);
 const params = { limit: 100 };
 if (projectId) params.project = projectId;

 const response = await api.get("/work-orders", { params });
 if (response.data.success) {
 setWorkOrders(response.data.data);
 }
 } catch (error) {
 console.error("Error fetching work orders:", error);
 } finally {
 setLoadingWO(false);
 }
 };

 const fetchDocument = async (docId) => {
 try {
 setLoadingDoc(true);
 const response = await api.get(`/document-control/${docId}`);
 if (response.data.success) {
 const doc = response.data.data;
 const selectedIds = [
 ...(Array.isArray(doc.boqItems) ? doc.boqItems : []),
 ...(doc.boqItem ? [doc.boqItem] : []),
 ]
 .map((item) => (item?._id || item)?.toString())
 .filter(Boolean);

 setForm({
 ...doc,
 project: doc.project?._id || doc.project || "",
 workOrder: doc.workOrder?._id || doc.workOrder || "",
 purchaseOrder: doc.purchaseOrder?._id || doc.purchaseOrder || "",
 docCategory: doc.docCategory || doc.documents?.[0]?.docType || (type === "TC" ? "TC" : "TDS"),
 boqItems: selectedIds,
 submittedDate: doc.submittedDate
 ? doc.submittedDate.split("T")[0]
 : "",
 approvedDate: doc.approvedDate ? doc.approvedDate.split("T")[0] : "",
 });
 setExistingDocuments(doc.documents || []);
 if (doc.workOrder) {
 const woId = doc.workOrder?._id || doc.workOrder;
 fetchBoqItems(woId, selectedIds, doc.wlino, doc.productName);
 fetchPurchaseOrders(woId);
 }
 }
 } catch (error) {
 console.error("Error loading document:", error);
 swal("Error", "Failed to load record", "error");
 } finally {
 setLoadingDoc(false);
 }
 };

 const fetchBoqItems = async (
 woId,
 initialSelectedIds = [],
 existingWlino = "",
 existingProductName = ""
 ) => {
 try {
 const response = await api.get(`/work-orders/${woId}`);
 if (response.data.success) {
 const items = response.data.data.boqItems || [];
 setBoqItems(items);

 setForm((prev) => {
 const currentIds = (prev.boqItems && prev.boqItems.length > 0)
 ? prev.boqItems
 : initialSelectedIds;

 const hasValidMatch = currentIds.some((id) =>
 items.some((b) => String(b._id) === String(id))
 );

 if (hasValidMatch) {
 return {
 ...prev,
 boqItems: currentIds
 .filter((id) => items.some((b) => String(b._id) === String(id)))
 .map(String),
 };
 }

 const wlinList = (existingWlino || prev.wlino || "")
 .split(",")
 .map((w) => w.trim())
 .filter(Boolean);
 const prodList = (existingProductName || prev.productName || "")
 .split(" / ")
 .map((p) => p.trim())
 .filter(Boolean);

 const matched = items.filter((b) => {
 const bWlin = (b.wlino || "").trim();
 const bName = (b.itemName || "").trim();
 if (bWlin && wlinList.includes(bWlin)) return true;
 if (bName && prodList.includes(bName)) return true;
 return false;
 });

 if (matched.length > 0) {
 return {
 ...prev,
 boqItems: matched.map((m) => m._id.toString()),
 };
 }
 return prev;
 });
 }
 } catch (error) {
 console.error("Error fetching BOQ items:", error);
 }
 };

 const fetchPurchaseOrders = async (woId) => {
 try {
 const response = await api.get(`/purchase-orders?workOrder=${woId}`);
 if (response.data.success) {
 setPurchaseOrders(response.data.data || []);
 }
 } catch (error) {
 console.error("Error fetching purchase orders:", error);
 }
 };

 const handleWorkOrderChange = (e) => {
 const woId = e.target.value;
 const wo = workOrders.find((w) => w._id === woId);
 setForm({
 ...form,
 workOrder: woId,
 project: wo?.project?._id || "",
 purchaseOrder: "",
 boqItems: [],
 boqItem: "",
 wlino: "",
 productName: "",
 quom: "",
 qty: "",
 });
 if (woId) {
 fetchBoqItems(woId);
 fetchPurchaseOrders(woId);
 } else {
 setBoqItems([]);
 setPurchaseOrders([]);
 }
 };

 const handleMultiBoqChange = (selectedOptions) => {
 const ids = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
 const selectedItems = boqItems.filter((item) => ids.includes(item._id));

 if (ids.length > 0) {
 const wlins = selectedItems
 .map((item) => item.wlino)
 .filter(Boolean)
 .join(", ");
 const productNames = [
 ...new Set(selectedItems.map((item) => item.itemName)),
 ].join(" / ");
 const totalQty = selectedItems.reduce(
 (sum, item) => sum + (Number(item.quantity) || 0),
 0,
 );
 const units = [...new Set(selectedItems.map((item) => item.unit))].join(
 ", ",
 );

 setForm((prev) => ({
 ...prev,
 boqItems: ids,
 wlino: wlins,
 productName: productNames,
 quom: units,
 qty: totalQty,
 }));
 } else {
 setForm((prev) => ({
 ...prev,
 boqItems: [],
 wlino: "",
 productName: "",
 quom: "",
 qty: "",
 }));
 }
 };

 const handleChange = (e) => {
 const { name, value } = e.target;
 setForm((prev) => ({ ...prev, [name]: value }));
 };

 const getFileUrl = (file) => getApiFileUrl(file?.fileUrl);

 const handleExistingDocumentDelete = async (docIndex) => {
 if (!id) return;
 try {
 const confirmed = await swal({
 title: "Are you sure?",
 text: "Once deleted, you will not be able to recover this document!",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (confirmed) {
 const response = await api.delete(`/document-control/${id}/documents/${docIndex}`);
 if (response.data.success) {
 setExistingDocuments((prev) => prev.filter((_, i) => i !== docIndex));
 swal("Poof! Your document has been deleted!", {
 icon: "success",
 });
 }
 }
 } catch (error) {
 console.error("Error deleting document:", error);
 swal("Error", "Failed to delete document", "error");
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!form.workOrder) {
 swal("Error", "Please select a work order", "error");
 return;
 }

 try {
 setLoading(true);
 const formData = new FormData();

 // Append basic fields
 Object.keys(form).forEach((key) => {
 if (form[key] !== null && form[key] !== undefined) {
 if (key === "document" || key === "documents") {
 // Skip, handled by newAttachments
 } else if (key === "boqItems" && Array.isArray(form[key])) {
 form[key].forEach((id) => {
 const cleanId = typeof id === "object" ? id._id : id;
 if (cleanId) formData.append("boqItems[]", cleanId);
 });
 } else if (!["existingDocument", "existingDocuments"].includes(key)) {
 let value = form[key];
 if (value && typeof value === "object" && value._id) {
 value = value._id;
 }
 // Ensure project is included even if it was missing in state
 if (key === "workOrder" && !form.project) {
 const wo = workOrders.find(w => w._id === value);
 if (wo?.project?._id) {
 formData.append("project", wo.project._id);
 }
 }
 formData.append(key, value);
 }
 }
 });

 // Add new attachments with metadata
 newAttachments.forEach((item) => {
 formData.append("documents", item.file);
 formData.append("attachmentTypes[]", item.docType);
 formData.append("attachmentRemarks[]", item.remarks);
 });

 formData.append("type", type);

 let response;
 if (editMode) {
 response = await api.put(`/document-control/${id}`, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.post("/document-control", formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 }

 if (response.data.success) {
 swal("Success", `Record ${editMode ? "updated" : "saved"} successfully`, "success");
 navigate(getReturnUrl());
 }
 } catch (error) {
 console.error("Error saving document:", error);
 swal("Error", error.response?.data?.message || "Failed to save record", "error");
 } finally {
 setLoading(false);
 }
 };

 const boqOptions = boqItems.map((item) => ({
 value: item._id,
 label: `[${item.wlino || "-"}] ${item.itemName} (${item.quantity} ${item.unit})`,
 }));

 const customSelectStyles = {
 control: (base) => ({
 ...base,
 minHeight: "45px",
 borderRadius: "8px",
 fontSize: "1rem",
 }),
 multiValue: (base) => ({
 ...base,
 backgroundColor: "#f1f1f1",
 }),
 placeholder: (base) => ({
 ...base,
 fontSize: "1rem",
 }),
 };

 return (
 <Fragment>
 <PageTitle
 activeMenu={editMode ? `Edit ${type === "TDS" ? "TDS/Manual" : "TC/Warranty Card"}` : `Upload ${type === "TDS" ? "TDS/Manual" : "TC/Warranty Card"}`}
 motherMenu="Document Control"
 />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>
 {editMode ? `Edit ${type === "TDS" ? "TDS/Manual" : "TC/Warranty Card"}` : `Upload ${type === "TDS" ? "TDS/Manual" : "TC/Warranty Card"}`}
 </Card.Title>
 <Button
 variant="secondary"
 onClick={() => navigate(getReturnUrl())}
 >
 Back
 </Button>
 </Card.Header>
 <Card.Body>
 {loadingDoc ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 </div>
 ) : (
 <Form onSubmit={handleSubmit}>
 <Row>
 {type === "TC" && (
 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Document Type <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 name="docCategory"
 value={form.docCategory || "TC"}
 onChange={(e) => {
 const val = e.target.value;
 setForm((prev) => ({ ...prev, docCategory: val }));
 setNewAttachments((prev) =>
 prev.map((att) => ({ ...att, docType: val }))
 );
 }}
 disabled={loading}
 className="form-control-lg"
 >
 <option value="TC">Test Certificate (TC)</option>
 <option value="Warranty Card">Warranty Card</option>
 </Form.Select>
 </Form.Group>
 </Col>
 )}
 <Col md={type === "TC" ? 3 : 4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Work Order <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 name="workOrder"
 value={form.workOrder}
 onChange={handleWorkOrderChange}
 required
 disabled={loading}
 className="form-control-lg"
 >
 <option value="">Select Work Order</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo} - {wo.project?.projectName}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={type === "TC" ? 3 : 4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Purchase Order (PO) (Optional)
 </Form.Label>
 <Form.Select
 name="purchaseOrder"
 value={form.purchaseOrder}
 onChange={handleChange}
 disabled={loading || !form.workOrder}
 className="form-control-lg"
 >
 <option value="">Select PO</option>
 {purchaseOrders.map((po) => (
 <option key={po._id} value={po._id}>
 {po.poNo} - {po.vendor?.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={type === "TC" ? 3 : 4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 BOQ Items <span className="text-danger">*</span>
 </Form.Label>
 <Select
 isMulti
 options={boqOptions}
 value={boqOptions.filter((opt) =>
 (form.boqItems || []).some(
 (id) => String(id?._id || id) === String(opt.value)
 )
 )}
 onChange={handleMultiBoqChange}
 className="basic-multi-select"
 classNamePrefix="select"
 isDisabled={loading || !form.workOrder}
 placeholder="Select one or more items..."
 styles={customSelectStyles}
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 WLIN (Auto-filled from selected items)
 </Form.Label>
 <Form.Control
 name="wlino"
 value={form.wlino}
 onChange={handleChange}
 disabled={loading}
 className="form-control-lg"
 />
 </Form.Group>
 </Col>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Product Name (Auto-filled){" "}
 <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 name="productName"
 value={form.productName}
 onChange={handleChange}
 required
 disabled={loading}
 className="form-control-lg"
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">UOM</Form.Label>
 <Form.Control
 name="quom"
 value={form.quom}
 onChange={handleChange}
 disabled={loading}
 className="form-control-lg"
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Total QTY <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="number"
 name="qty"
 value={form.qty}
 onChange={handleChange}
 required
 disabled={loading}
 className="form-control-lg"
 />
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Submitted Date
 </Form.Label>
 <Form.Control
 type="date"
 name="submittedDate"
 value={form.submittedDate}
 onChange={handleChange}
 disabled={loading}
 className="form-control-lg"
 />
 </Form.Group>
 </Col>

 {type === "TDS" && (
 <>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Approved Date
 </Form.Label>
 <Form.Control
 type="date"
 name="approvedDate"
 value={form.approvedDate}
 onChange={handleChange}
 disabled={loading}
 className="form-control-lg"
 />
 </Form.Group>
 </Col>

 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">
 Status
 </Form.Label>
 <Form.Select
 name="status"
 value={form.status}
 onChange={handleChange}
 disabled={loading}
 className="form-control-lg"
 >
 <option value="Pending Approval">
 Pending Approval
 </option>
 </Form.Select>
 </Form.Group>
 </Col>
 </>
 )}
 </Row>

 <Row>
 <Col md={12}>
 <Form.Group className="mb-4">
 <Form.Label className="font-w600">
 {type === "TDS" ? "TDS Documents" : (form.docCategory === "Warranty Card" ? "Warranty Card Documents" : "TC Documents")}{" "}
 {!editMode &&
 existingDocuments.length === 0 &&
 newAttachments.length === 0 && (
 <span className="text-danger">*</span>
 )}
 </Form.Label>
 <div className="custom-file-upload border-dashed rounded p-3 text-center mb-3">
 <Form.Control
 type="file"
 id="doc-upload"
 onChange={handleFileChange}
 disabled={loading}
 multiple
 accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.dgn,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif"
 className="d-none"
 />
 <label htmlFor="doc-upload" className="mb-0 cursor-pointer">
 <i className="fas fa-cloud-upload-alt fa-2x text-primary mb-2"></i>
 <p className="mb-0">Click to upload or drag and drop</p>
 <small className="text-muted">PDF, DOC, XLS, DWG/DXF/DGN Drawings, ZIP/RAR Archives, Images up to 10MB</small>
 </label>
 </div>

 {/* NEW ATTACHMENTS TABLE */}
 {newAttachments.length > 0 && (
 <div className="table-responsive mb-4">
 <table className="table table-bordered table-sm">
 <thead className="bg-light">
 <tr>
 <th>File Name</th>
 <th style={{ width: "180px" }}>Type</th>
 <th>Remarks</th>
 <th style={{ width: "50px" }}></th>
 </tr>
 </thead>
 <tbody>
 {newAttachments.map((item, index) => (
 <tr key={index}>
 <td className="align-middle small">{item.fileName}</td>
 <td>
 {type === "TDS" ? (
 <Form.Select
 size="sm"
 value={item.docType}
 onChange={(e) =>
 updateNewAttachment(index, "docType", e.target.value)
 }
 >
 <option value="TDS">TDS</option>
 <option value="Manual">Manual</option>
 </Form.Select>
 ) : (
 <Form.Select
 size="sm"
 value={item.docType || form.docCategory || "TC"}
 onChange={(e) =>
 updateNewAttachment(index, "docType", e.target.value)
 }
 >
 <option value="TC">Test Certificate (TC)</option>
 <option value="Warranty Card">Warranty Card</option>
 </Form.Select>
 )}
 </td>
 <td>
 <Form.Control
 size="sm"
 type="text"
 placeholder="Add remarks..."
 value={item.remarks}
 onChange={(e) =>
 updateNewAttachment(index, "remarks", e.target.value)
 }
 />
 </td>
 <td className="text-center">
 <Button
 variant="danger"
 size="sm"
 onClick={() => removeNewAttachment(index)}
 >
 <i className="fas fa-times"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}

 {/* EXISTING DOCUMENTS TABLE */}
 {existingDocuments.length > 0 && (
 <div className="mt-3">
 <h6 className="font-w600 mb-3">Existing Documents</h6>
 <div className="table-responsive">
 <table className="table table-bordered table-sm">
 <thead className="bg-light">
 <tr>
 <th>File Name</th>
 <th>Type</th>
 <th>Remarks</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>
 <tbody>
 {existingDocuments.map((file, index) => (
 <tr key={index}>
 <td className="align-middle small">{file.fileName}</td>
 <td className="align-middle">
 {type === "TDS" ? (
 <span className="badge badge-info">
 {file.docType === "Module" ? "Manual" : (file.docType || "TDS")}
 </span>
 ) : (
 <span className={`badge ${file.docType === "Warranty Card" ? "badge-warning text-dark" : "badge-primary"}`}>
 {file.docType || form.docCategory || "TC"}
 </span>
 )}
 </td>
 <td className="align-middle small">{file.remarks || "-"}</td>
 <td className="text-center">
 <div className="d-flex justify-content-center gap-1">
 <a
 href={getFileUrl(file)}
 target="_blank"
 rel="noreferrer"
 className="btn btn-primary btn-xxs shadow"
 title="View"
 >
 <i className="fas fa-eye"></i>
 </a>
 <a
 href={getFileUrl(file)}
 download={file.fileName}
 className="btn btn-success btn-xxs shadow"
 title="Download"
 >
 <i className="fas fa-download"></i>
 </a>
 <Button
 variant="danger"
 className="btn-xxs shadow"
 disabled={loading}
 onClick={() => handleExistingDocumentDelete(index)}
 title="Delete"
 >
 <i className="fas fa-trash"></i>
 </Button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 )}
 </Form.Group>
 </Col>
 </Row>

 <Row>
 <Col md={12}>
 <Form.Group className="mb-3">
 <Form.Label className="font-w600">Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 name="remarks"
 value={form.remarks}
 onChange={handleChange}
 disabled={loading}
 placeholder="Enter any additional remarks..."
 className="form-control-lg"
 />
 </Form.Group>
 </Col>
 </Row>

 <div className="text-end">
 <Button
 variant="light"
 type="button"
 disabled={loading}
 className="me-2 px-4"
 onClick={() => navigate(getReturnUrl())}
 >
 Cancel
 </Button>
 <Button
 variant="primary"
 type="submit"
 disabled={loading}
 className="px-5"
 >
 {loading ? (
 <Fragment>
 <div className="spinner-border spinner-border-sm me-2"></div>{" "}
 Processing...
 </Fragment>
 ) : editMode ? (
 `Update ${type === "TDS" ? "TDS/Manual" : "TC/Warranty Card"}`
 ) : (
 `Submit ${type === "TDS" ? "TDS/Manual" : "TC/Warranty Card"}`
 )}
 </Button>
 </div>
 </Form>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default DocumentControlUpload;
